import React, { useState, useMemo, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Input, Select, Checkbox, Breadcrumb, notification, Modal } from 'antd'
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons'
import {
  CardElement,
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { LoadAllCountry } from '../../components/_utils/countryUtil'
import { applyPackagesFxn, payPaymentFxn, updateEsimPaymentFxn } from './actions'
import './esim.css'
import { filterOption } from '../../components/_utils/appUtils'
import { StripePromise } from '../../components/_utils/utils'

const { Option } = Select


const useStripeOptions = () => {
  return useMemo(() => ({
    iconStyle: 'solid',
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#0f52ba',
        letterSpacing: '0.025em',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': { color: '#aab7c4' },
        padding: '10px 12px',
        lineHeight: '24px',
        iconColor: '#0f52ba',
        fontSmoothing: 'antialiased'
      },
      invalid: {
        color: '#e5424d',
        ':focus': {
          color: '#303238'
        }
      }
    }
  }), [])
}

const EsimCheckoutForm = () => {
  const history = useHistory()
  const location = useLocation()
  const stripe = useStripe()
  const elements = useElements()
  const options = useStripeOptions()
  const dispatch = useDispatch()
  const { selectedPackage, countryDetails, quantity: initialQuantity = 1 } = location.state || {}

  // Local quantity state
  const [quantity, setQuantity] = useState(initialQuantity)

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    homeCountry: '',
    agreeToOffers: false,
    agreeToTerms: false,
    cardHolderName: ''
  })
  const CARD_ELEMENT_OPTIONS = {
    iconStyle: 'solid',
    hidePostalCode: true,
    style: {
      base: {},
      invalid: {
        color: '#e5424d',
        ':focus': {
          color: '#303238'
        }
      }
    }
  }

  const [couponCode, setCouponCode] = useState('')
  const [processing, setProcessing] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)

  const packageData = selectedPackage || {
    data: '5',
    amount: '13',
    day: '14',
    country_id: 'al'
  }

  const packageCountry = countryDetails || {
    name: 'Albania',
    image: 'https://images.unsplash.com/photo-1551524164-c38da4d760f6?w=600&h=400&fit=crop'
  }

  const subtotal = parseFloat(packageData.amount) * quantity
  const total = subtotal

  useEffect(() => {
    loadCountryList()
  }, [])

  const loadCountryList = async () => {
    try {
      setLoadingCountries(true)
      const countries = LoadAllCountry()
      const sortedCountries = countries.sort((a, b) => a.name.localeCompare(b.name))
      setCountryList(sortedCountries)
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load countries. Please refresh and try again.'
      })
    } finally {
      setLoadingCountries(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const updateQuantity = (change) => {
    setQuantity(prev => Math.max(1, prev + change))
  }

  const validateForm = () => {
    const requiredFields = ['email', 'firstName', 'lastName', 'homeCountry', 'cardHolderName']
    const missingFields = requiredFields.filter(field => !formData[field])

    if (missingFields.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: 'Please fill in all required fields'
      })
      return false
    }

    if (!formData.agreeToTerms) {
      notification.error({
        message: 'Agreement Required',
        description: 'Please agree to terms and conditions'
      })
      return false
    }

    if (!stripe || !elements) {
      notification.error({
        message: 'Payment System Error',
        description: 'Payment system is not ready. Please try again.'
      })
      return false
    }

    return true
  }


  const handlePayNow = async () => {
    if (!validateForm()) return
    setProcessing(true)
    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }
      const { error, token } = await stripe.createToken(cardElement)
      if (error) {
        throw new Error(error.message)
      } else {
        const paymentData = {
          token: token,
          amount: total,
          customerData: formData,
          packageData,
          quantity,
          cardHolderName: formData.cardHolderName
        }
        const { data, esimPaymentId } = await dispatch(payPaymentFxn(paymentData))
        const cardElement = elements.getElement(CardElement)
        const confirmPayment = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: { card: cardElement }
        })
        const { paymentIntent } = confirmPayment
        if (paymentIntent && paymentIntent.status && paymentIntent.status == 'succeeded') {
          const applyPackagePayload = {
            cardToken: token,
            package_id: packageData.package_id,
            origin_country_code: formData.homeCountry,
            email: formData.email,
            firstname: formData.firstName,
            lastname: formData.lastName,
            quantity: quantity,
            amount: total,
            cardHolderName: formData.cardHolderName,
            paymentResponse: paymentIntent,
            esimPaymentId: esimPaymentId
          }

          let obj = {
            esimPaymentId: esimPaymentId,
            status: 'failed',
            response: ''
          }
          if (paymentIntent) {
            obj.status = paymentIntent.status
            obj.paymentResponse = paymentIntent
          } else {
            obj.paymentResponse = confirmPayment
          }
          let resp = await dispatch(updateEsimPaymentFxn(obj))

          const packageResponse = await dispatch(applyPackagesFxn(applyPackagePayload))

          if (packageResponse.success || packageResponse.data) {
            notification.success({
              message: 'Success',
              description: packageResponse.message || 'Success'
            })

            const resp = {
              packageResponse: packageResponse,
              packageData: packageData,
              quantity: quantity,
              total: total,
              customerData: formData,
              paymentResponse: paymentIntent
            }

            dispatch({ type: 'SET_PACKAGE_RESP', payload: resp })
            history.push(`/esim-success/${packageResponse.esimId}`, {
              packageResponse: packageResponse,
              packageData: packageData,
              quantity: quantity,
              total: total,
              customerData: formData,
              paymentResponse: paymentIntent
            })
          } else {
            throw new Error('Package application failed. Please try again.')
          }
        } else {
          let obj = {
            esimPaymentId: esimPaymentId,
            status: 'failed',
            response: ''
          }
          if (paymentIntent) {
            obj.status = paymentIntent.status
            obj.paymentResponse = paymentIntent
          } else {
            obj.paymentResponse = confirmPayment
          }
          let resp = await dispatch(updateEsimPaymentFxn(obj))
          Modal.error({
            title: 'Payment Fail'
          })
        }
      }

    } catch (error) {

      if (error.message.includes('package')) {
        notification.error({
          message: 'Package Application Failed',
          description: error.message || 'Failed to apply package. Please try again.'
        })
      } else if (error.message.includes('card') || error.message.includes('payment')) {
        notification.error({
          message: 'Payment Failed',
          description: error.message || 'Payment Failed'
        })
      } else {
        notification.error({
          message: 'Error',
          description: error.message || 'An unexpected error occurred. Please try again.'
        })
      }
    } finally {
      setProcessing(false)
    }
  }

  const applyCoupon = () => {
    if (!couponCode.trim()) {
      notification.warning({
        message: 'Coupon Required',
        description: 'Please enter a coupon code'
      })
      return
    }
    // Add coupon logic here
    notification.success({
      message: 'Coupon Applied',
      description: 'Coupon applied successfully!'
    })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className='esim-checkout-container'>
      <div className='esim-checkout-wrapper'>

        {/* Breadcrumb */}
        <div className='esim-checkout-breadcrumb'>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Button
                type='link'
                icon={<ArrowLeftOutlined />}
                onClick={() => history.goBack()}
                style={{ padding: 0, color: '#1556BC' }}
              >
                Home
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Checkout</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Main Content */}
        <div className='esim-checkout-content'>
          {/* Payment Form */}
          <div className='esim-checkout-form-section'>
            <div className='esim-checkout-form-card'>
              <div className='esim-form-content'>

                {/* Notice */}
                <div className='esim-form-notice'>
                  Please ensure you use a valid email address as your eSIM receipt and support instructions will be sent
                  to this address.
                </div>

                {/* Email */}
                <div className='esim-form-group'>
                  <label className='esim-form-label'>Email *</label>
                  <Input
                    placeholder='Enter Email'
                    className='form-control'
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    type='email'
                  />
                </div>

                {/* Name Fields */}
                <div className='esim-form-row'>
                  <div className='esim-form-col'>
                    <div className='esim-form-group'>
                      <label className='esim-form-label'>First name *</label>
                      <Input
                        placeholder='First Name'
                        className='form-control'
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className='esim-form-col'>
                    <div className='esim-form-group'>
                      <label className='esim-form-label'>Last name *</label>
                      <Input
                        placeholder='Last Name'
                        className='form-control'
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Card Holder Name */}
                <div className='esim-form-group'>
                  <label className='esim-form-label'>Card Holder Name *</label>
                  <Input
                    placeholder='Enter Card Holder Name'
                    className='form-control'
                    value={formData.cardHolderName}
                    onChange={(e) => handleInputChange('cardHolderName', e.target.value)}
                  />
                </div>

                {/* Stripe Card Element */}
                <div className='esim-card-details'>
                  <div className='esim-form-group'>
                    <label className='esim-form-label'>
                      Card Details *
                      <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                        (International Payment Enabled - Debit/Credit Card Only)
                      </small>
                    </label>
                    <div className='esim-stripe-element'>
                      <CardElement options={options} />
                    </div>
                  </div>
                </div>

                {/* Home Country */}
                <div className='esim-form-group'>
                  <label className='esim-form-label'>Select home country *</label>
                  <Select
                    placeholder='Select Home Country'
                    style={{ width: '100%' }}
                    value={formData.homeCountry}
                    onChange={(value) => handleInputChange('homeCountry', value)}
                    allowClear
                    showSearch
                    loading={loadingCountries}
                    filterOption={filterOption}
                  >
                    {countryList.map((item, key) => (
                      <Option value={item.sortname} key={key}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Checkboxes */}
                <div className='esim-checkbox-group'>
                  <label className='esim-checkbox-label'>
                    <Checkbox
                      checked={formData.agreeToOffers}
                      onChange={(e) => handleInputChange('agreeToOffers', e.target.checked)}
                    />
                    Please keep me informed of any upcoming offers or promotions
                  </label>
                </div>

                <div className='esim-checkbox-group'>
                  <label className='esim-checkbox-label'>
                    <Checkbox
                      checked={formData.agreeToTerms}
                      onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    />
                    I agree to the <a href='/terms' target='_blank' rel='noopener noreferrer'>Terms and Conditions</a> *
                  </label>
                </div>

                {/* Pay Button */}
                <button
                  className={`esim-pay-button ${processing ? 'processing' : ''}`}
                  onClick={handlePayNow}
                  disabled={processing || !stripe}>
                  {processing ? 'Processing...' : `Pay now - £${total.toFixed(2)}`}
                </button>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className='esim-order-summary-section'>
            <div className='esim-order-summary-card'>
              <h3 className='esim-order-summary-title'>Order Summary</h3>

              {/* Order Item */}
              <div className='esim-order-item'>
                <div className='esim-order-item-flag'>
                  <img
                    src={`https://flagcdn.com/48x36/${packageData.country_id.toLowerCase()}.png`}
                    alt={`${packageCountry.name} flag`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div className='esim-order-item-details'>
                  <div className='esim-order-item-country'>{packageCountry.name}</div>
                  <div className='esim-order-item-data'>{packageData.data} GB</div>
                  <div className='esim-order-item-data'>Data Only</div>
                  <div className='esim-order-item-validity'>{packageData.day} Days</div>
                </div>
                <div className='esim-order-item-quantity'>
                  <div className='esim-checkout-quantity-controls'>
                    {/* Quantity controls can be uncommented if needed */}
                  </div>
                </div>
              </div>

              {/* Coupon Section */}
              <div className='esim-coupon-section'>
                <div className='esim-coupon-label'>Coupon code</div>
                <div className='esim-coupon-input-group'>
                  <Input
                    placeholder='Enter Coupon Code'
                    className='form-control'
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button className='esim-coupon-button' onClick={applyCoupon}>
                    Apply Coupon
                  </button>
                </div>
              </div>

              {/* Order Totals */}
              <div className='esim-order-totals'>
                <div className='esim-total-row'>
                  <span>Sub total ({quantity} × £{packageData.amount})</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>
                <div className='esim-total-row final'>
                  <span>Total</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className='esim-trust-section'>
                <div className='esim-guarantee'>
                  <div className='esim-guarantee-icon'>
                    <CheckOutlined />
                  </div>
                  <div className='esim-guarantee-text'>
                    <div className='esim-guarantee-title'>Money-back guarantee, risk-free</div>
                    <div className='esim-guarantee-desc'>
                      Your satisfaction is guaranteed! If something doesn't work as expected, we'll give you a full
                      refund.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const EsimCheckout = () => {
  /*  const [stripePromise, setStripePromise] = useState(null)

    useEffect(() => {
      setStripePromise(loadStripe(STRIPE_PUBLIC_KEY))
    }, [])

    if (!stripePromise) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          fontSize: '16px'
        }}>
          Loading payment system...
        </div>
      )
    }*/

  return (
    <Elements stripe={StripePromise}>
      <EsimCheckoutForm />
    </Elements>
  )
}

export default EsimCheckout
